import { Card, CardBody, CardHead } from "@/components/admin/card";
import { PageHead } from "@/components/admin/page-head";
import { fmtFCFA } from "@/lib/format";
import {
	useOrdersAnalytics,
	useOrdersRealtime,
} from "@/lib/orders/orders-hooks";
import { statusOrderV2 } from "@/lib/status";

export function AdminOrdersStatsPage() {
	const { data: rt } = useOrdersRealtime();
	const { data: an } = useOrdersAnalytics();

	const maxRev = Math.max(1, ...(an?.series ?? []).map((s) => s.revenue));

	return (
		<>
			<PageHead
				title="Statistiques commandes"
				sub="ORD-08 — temps réel + analytique 30j"
			/>

			<div
				className="kpi-grid"
				style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
			>
				<div className="kpi">
					<div className="kpi-l">Aujourd'hui</div>
					<div className="kpi-v tn">{rt?.todayCount ?? 0}</div>
					<div className="kpi-foot muted">{fmtFCFA(rt?.todayRevenue ?? 0)}</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">SLA en retard</div>
					<div className="kpi-v tn">{rt?.slaLate ?? 0}</div>
					<div className="kpi-foot muted">à débloquer</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Total créées</div>
					<div className="kpi-v tn">{an?.funnel.created ?? 0}</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Livrées</div>
					<div className="kpi-v tn">{an?.funnel.delivered ?? 0}</div>
				</div>
			</div>

			<div className="stack">
				<Card>
					<CardHead title="Par statut (temps réel)" />
					<CardBody>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
								gap: 10,
							}}
						>
							{Object.entries(rt?.byStatus ?? {}).map(([status, count]) => {
								const meta = statusOrderV2(status);
								return (
									<div
										key={status}
										style={{
											padding: 10,
											border: "1px solid var(--a-border)",
											borderRadius: 6,
										}}
									>
										<div className="muted" style={{ fontSize: 11 }}>
											{meta.l}
										</div>
										<div className="tn" style={{ fontSize: 20 }}>
											{count}
										</div>
									</div>
								);
							})}
						</div>
					</CardBody>
				</Card>

				<Card>
					<CardHead title="Funnel (cumul depuis création)" />
					<CardBody>
						<div className="funnel-chart">
							{an &&
								Object.entries(an.funnel).map(([k, v]) => {
									const total = an.funnel.created || 1;
									const ratio = v / total;
									const barH = Math.max(6, Math.round(ratio * 140));
									return (
										<div key={k} className="funnel-col">
											<div className="funnel-val tn">{v}</div>
											<div className="funnel-bar-track">
												<div
													className="funnel-bar"
													style={{ height: `${barH}px` }}
												/>
											</div>
											<div className="funnel-label muted">{k}</div>
										</div>
									);
								})}
						</div>
					</CardBody>
				</Card>

				<Card>
					<CardHead title="Séries 30 derniers jours" />
					<CardBody>
						{(an?.series ?? []).length === 0 ? (
							<div
								className="muted"
								style={{ padding: 24, textAlign: "center" }}
							>
								Aucune donnée sur les 30 derniers jours.
							</div>
						) : (
							<>
								<div className="series-chart">
									{(an?.series ?? []).map((s) => {
										const h = Math.max(
											4,
											Math.round((s.revenue / maxRev) * 140),
										);
										return (
											<div key={s.day} className="series-col">
												<div
													title={`${new Date(s.day).toLocaleDateString("fr-FR")} : ${s.count} cmd · ${fmtFCFA(s.revenue)}`}
													className="series-bar"
													style={{ height: `${h}px` }}
												/>
											</div>
										);
									})}
								</div>
								<div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
									Hauteur = revenu jour ({(an?.series ?? []).length} jour(s)).
									Survolez pour le détail.
								</div>
							</>
						)}
					</CardBody>
				</Card>
			</div>
			<style>{`
        .funnel-chart {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 12px;
          align-items: end;
          min-height: 200px;
        }
        .funnel-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .funnel-val {
          font-size: 13px;
          font-weight: 600;
        }
        .funnel-bar-track {
          width: 80%;
          max-width: 80px;
          height: 140px;
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }
        .funnel-bar {
          width: 100%;
          background: var(--a-terracotta);
          border-radius: 6px 6px 0 0;
          min-height: 6px;
        }
        .funnel-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .series-chart {
          display: flex;
          gap: 2px;
          align-items: flex-end;
          height: 160px;
          padding: 8px 4px;
          background: var(--a-bg);
          border-radius: 6px;
        }
        .series-col {
          flex: 1;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          min-width: 0;
        }
        .series-bar {
          width: 100%;
          max-width: 18px;
          background: var(--a-terracotta);
          border-radius: 2px 2px 0 0;
          transition: filter 120ms;
        }
        .series-bar:hover { filter: brightness(1.15); }
      `}</style>
		</>
	);
}
